import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { getSavedScrollY } from '@theme/scroll-lock';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration | null} */
  #previousScrollRestoration = null;

  /** @type {number} */
  #preservedScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    // Snapshot while scroll-lock still holds the PLP offset (before any unlock/re-lock race).
    this.#preservedScrollY = getSavedScrollY();

    if (!history.state?.cartDrawerOpen) {
      // pushState while scroll-lock has body position:fixed reports scrollY as 0.
      // With scrollRestoration "auto", history.back() on close jumps the PLP to top.
      this.#previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      history.pushState({ cartDrawerOpen: true, scrollY: this.#preservedScrollY }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #restoreScrollRestoration() {
    if (this.#previousScrollRestoration == null) return;
    history.scrollRestoration = this.#previousScrollRestoration;
    this.#previousScrollRestoration = null;
  }

  /**
   * iOS Safari often applies history scroll restoration after popstate returns.
   * Re-apply the PLP offset across a few frames/timeouts before handing scrollRestoration back.
   * @param {number} scrollY
   */
  #applyPreservedScroll(scrollY) {
    const apply = () => {
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      const scrollingElement = document.scrollingElement;
      if (scrollingElement) scrollingElement.scrollTop = scrollY;
    };

    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
    for (const ms of [0, 50, 100, 200]) {
      setTimeout(apply, ms);
    }
    setTimeout(() => this.#restoreScrollRestoration(), 250);
  }

  #handleHistoryClose = (event) => {
    this.#historyAbortController?.abort();

    const scrollY = this.#preservedScrollY || getSavedScrollY();

    if (!history.state?.cartDrawerOpen) {
      // Browser back already popped our state; still pin scroll (unlock may have raced to 0).
      this.#applyPreservedScroll(scrollY);
      return;
    }

    // Replacing with another drawer: clear our flag without history.back().
    // A back+pushState race would popstate-close the drawer that just opened.
    if (event instanceof DialogCloseEvent && event.detail?.reason === 'replace') {
      history.replaceState(null, '');
      this.#restoreScrollRestoration();
      return;
    }

    const restoreScroll = () => {
      this.#applyPreservedScroll(scrollY);
    };

    window.addEventListener('popstate', restoreScroll, { once: true });
    history.back();
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
